import {
  formatPhoneNumberForStore,
  formatPhoneNumberInternational,
  getParsedDateAsISO,
  safeParseDate,
  normalizeBirthDateInput,
} from '@lili/utils/formatting';
import { normalizeSsnInput } from '@lili/utils/validation';
import { OnboardingFormFields } from '@Types/index';
import { UBORoles } from '@Store/index';
import { DEFAULT_DATE_FORMAT } from '@Consts/constants';
import type { Country } from '@Types/country';
import type { RegionCode } from 'google-libphonenumber';
import type { UboResponse } from '@Store/index';
import type { UBOFormFields } from './UBOFormScheme';
import type { SaveOwnerBody } from '@Types/owner';

export const prepareUBOFormValuesToServer = (
  values: UBOFormFields,
  uboId?: string
): SaveOwnerBody => {
  const isUsResident = values.isUsResident;

  return {
    ...(uboId && { externalId: uboId }),
    email: values[OnboardingFormFields.EMAIL],
    phoneNumber: formatPhoneNumberForStore(values[OnboardingFormFields.PHONE_NUMBER]),
    phoneNumberCountryCode: values[OnboardingFormFields.PHONE_NUMBER_ISO_CODE] || 'US',
    customerPersonalInfo: {
      firstName: values[OnboardingFormFields.FIRST_NAME],
      lastName: values[OnboardingFormFields.LAST_NAME],
    },
    customerSSN: {
      ssn: isUsResident ? normalizeSsnInput(values[OnboardingFormFields.SSN] || '') : undefined,
      birthDate: normalizeBirthDateInput(
        getParsedDateAsISO(values[OnboardingFormFields.DATE_OF_BIRTH])
      ),
      idGovNumber: !isUsResident ? values[OnboardingFormFields.NATIONAL_ID_NUMBER] : undefined,
      idType: !isUsResident ? 'passport' : undefined,
      idNumber: !isUsResident ? values[OnboardingFormFields.PASSPORT_NUMBER] : undefined,
      idCountry: !isUsResident ? values[OnboardingFormFields.NATIONALITY] : undefined,
    },
    customerAddress: {
      city: values[OnboardingFormFields.CITY],
      state: values[OnboardingFormFields.STATE]?.value ?? '',
      line1: values[OnboardingFormFields.LINE1],
      line2: values[OnboardingFormFields.APARTMENT] ?? '',
      postalCode: values[OnboardingFormFields.ZIP],
      sameAsPrimary: false,
      country: isUsResident ? 'US' : values[OnboardingFormFields.COUNTRY]?.code,
    },
    role: UBORoles.PARTNER,
    ownershipPercent: Number(values[OnboardingFormFields.OWNER_PERCENTAGE]),
  };
};

export const mapUBOToFormFields = (
  ubo: UboResponse | undefined | null,
  isUsResident: boolean,
  countries: Country[]
): UBOFormFields => ({
  [OnboardingFormFields.FIRST_NAME]: ubo?.firstName || '',
  [OnboardingFormFields.LAST_NAME]: ubo?.lastName || '',
  [OnboardingFormFields.EMAIL]: ubo?.email || '',
  [OnboardingFormFields.OWNER_PERCENTAGE]: ubo?.ownershipPercent || 25,
  isUsResident,
  [OnboardingFormFields.SSN]: ubo?.ssn || '',
  [OnboardingFormFields.NATIONALITY]: ubo?.customerSSNData?.idCountry || '',
  [OnboardingFormFields.PASSPORT_NUMBER]: ubo?.customerSSNData?.idNumber || '',
  [OnboardingFormFields.NATIONAL_ID_NUMBER]: ubo?.customerSSNData?.idGovNumber || '',
  [OnboardingFormFields.DATE_OF_BIRTH]: ubo?.birthDate
    ? safeParseDate(ubo?.birthDate, DEFAULT_DATE_FORMAT)
    : '',
  [OnboardingFormFields.PHONE_NUMBER]: formatPhoneNumberInternational(ubo?.phoneNumber || '') || '',
  [OnboardingFormFields.LINE1]: ubo?.customerAddressData?.line1 || '',
  [OnboardingFormFields.APARTMENT]: ubo?.customerAddressData?.line2 || '',
  [OnboardingFormFields.CITY]: ubo?.customerAddressData?.city || '',
  [OnboardingFormFields.STATE]: ubo?.customerAddressData?.state
    ? { value: ubo?.customerAddressData.state, title: ubo?.customerAddressData.state }
    : null,
  [OnboardingFormFields.COUNTRY]: ubo?.customerAddressData?.country
    ? findCountryByCode(ubo?.customerAddressData.country, countries)
    : null,
  [OnboardingFormFields.ZIP]: ubo?.customerAddressData?.postalCode || '',
  [OnboardingFormFields.PHONE_NUMBER_ISO_CODE]: (ubo?.phoneNumberCountryCode as RegionCode) || 'US',
});

export const findCountryByCode = (code: string, countries: Country[] | undefined) => {
  if (!countries) return undefined;
  return countries.find((country) => country.code === code);
};
